import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Save, FileDown, Edit2, CheckCircle, Clock, Share2 } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';

// Import section editors
import BasicInfoEditor from '@/components/editors/BasicInfoEditor';
import ProjectTeamEditor from '@/components/editors/ProjectTeamEditor';
import SchedulesEditor from '@/components/editors/SchedulesEditor';
import GeneralNotesEditor from '@/components/editors/GeneralNotesEditor';
import RendersEditor from '@/components/editors/RendersEditor';
import ComplianceEditor from '@/components/editors/ComplianceEditor';
import SheetIndexEditor from '@/components/editors/SheetIndexEditor';
import VisualEditor from '@/components/editors/VisualEditor';

const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  
  useEffect(() => {
    loadProject();
  }, [id]);
  
  const loadProject = async () => {
    try {
      setLoading(true);
      const data = await api.getProject(id);
      setProject(data);
    } catch (error) {
      toast.error('Error loading project');
      console.error('Error:', error);
      navigate('/');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSave = async () => {
    try {
      setSaving(true);
      await api.updateProject(id, project);
      toast.success('Project saved successfully!');
    } catch (error) {
      toast.error('Error saving project');
      console.error('Error:', error);
    } finally {
      setSaving(false);
    }
  };
  
  const validateProject = () => {
    const errors = [];
    
    // Check basic required fields
    if (!project.name || project.name.trim() === '') {
      errors.push('Project name is required');
    }
    
    if (!project.project_type) {
      errors.push('Project type is required');
    }
    
    if (!project.address || project.address.trim() === '') {
      errors.push('Project address is required');
    }
    
    // Check if at least one sheet is included
    const hasIncludedSheets = project.sheets?.some(sheet => sheet.included);
    if (!hasIncludedSheets) {
      errors.push('At least one sheet must be included');
    }
    
    return errors;
  };
  
  const handleStatusChange = async (newStatus) => {
    // Validate project before changing to in_progress
    if (newStatus === 'in_progress' && project.status === 'draft') {
      const validationErrors = validateProject();
      
      if (validationErrors.length > 0) {
        toast.error('Cannot start project', {
          description: (
            <div>
              <p className="font-semibold mb-2">Please fix the following:</p>
              <ul className="list-disc pl-4 space-y-1">
                {validationErrors.map((error, idx) => (
                  <li key={idx}>{error}</li>
                ))}
              </ul>
            </div>
          ),
          duration: 5000,
        });
        return;
      }
    }
    
    try {
      await api.updateProject(id, { status: newStatus });
      setProject({ ...project, status: newStatus });
      toast.success(`Project marked as ${newStatus.replace('_', ' ')}`);
    } catch (error) {
      toast.error('Error updating status');
    }
  };
  
  const handleExportPDF = () => {
    toast.info('PDF export will be implemented soon');
    // TODO: Implement PDF generation
  };
  
  const handleGenerateShareCode = async () => {
    try {
      if (project.share_code) {
        // Copy link to clipboard
        const shareUrl = `${window.location.origin}/shared/${project.share_code}`;
        navigator.clipboard.writeText(shareUrl);
        toast.success('Share link copied to clipboard!', {
          description: 'Share this link with your team to allow them to view the project, upload files, and add comments.',
          duration: 5000,
        });
        // Also open in new tab
        window.open(`/shared/${project.share_code}`, '_blank');
      } else {
        // Generate new share code
        const response = await api.generateShareCode(id);
        setProject({ ...project, share_code: response.share_code });
        const shareUrl = `${window.location.origin}/shared/${response.share_code}`;
        navigator.clipboard.writeText(shareUrl);
        toast.success('Share link created and copied!', {
          description: 'Share this link with your team to allow them to view the project, upload files, and add comments.',
          duration: 5000,
        });
        window.open(`/shared/${response.share_code}`, '_blank');
      }
    } catch (error) {
      toast.error('Error generating share link');
      console.error('Error:', error);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-slate-600 dark:text-slate-400">Loading project...</p>
        </div>
      </div>
    );
  }
  
  if (!project) {
    return null;
  }
  
  const statusColors = {
    draft: 'bg-amber-50 text-amber-700 border border-amber-200',
    in_progress: 'bg-blue-50 text-[#1e40af] border border-blue-200',
    completed: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  };
  
  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/')}
            data-testid="back-btn"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          
          <div>
            <div className="flex items-center space-x-3 mb-2">
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white">
                {project.name}
              </h1>
              <Badge className={statusColors[project.status]}>
                {t(project.status)}
              </Badge>
            </div>
            
            <div className="flex items-center space-x-4 text-sm text-slate-600 dark:text-slate-400">
              <span>{t(project.project_type)}</span>
              {project.address && (
                <span>• {project.address}</span>
              )}
              {project.project_number && (
                <span>• #{project.project_number}</span>
              )}
            </div>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          {/* Status Actions */}
          {project.status === 'draft' && (
            <Button
              variant="outline"
              onClick={() => handleStatusChange('in_progress')}
              data-testid="start-project-btn"
            >
              <Clock className="mr-2 h-4 w-4" />
              Start Project
            </Button>
          )}
          
          {project.status === 'in_progress' && (
            <Button
              variant="outline"
              onClick={() => handleStatusChange('completed')}
              data-testid="complete-project-btn"
            >
              <CheckCircle className="mr-2 h-4 w-4" />
              Mark Complete
            </Button>
          )}
          
          <Button
            variant="outline"
            onClick={handleGenerateShareCode}
            data-testid="share-btn"
          >
            <Share2 className="mr-2 h-4 w-4" />
            {project.share_code ? 'View Link' : 'Generate Link'}
          </Button>
          
          <Button
            variant="outline"
            onClick={handleExportPDF}
            data-testid="export-pdf-btn"
          >
            <FileDown className="mr-2 h-4 w-4" />
            {t('export')}
          </Button>
          
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#1e40af] hover:bg-[#1e3a8a] text-white"
            data-testid="save-project-btn"
          >
            <Save className="mr-2 h-4 w-4" />
            {saving ? 'Saving...' : t('save')}
          </Button>
        </div>
      </div>
      
      {/* Content Tabs */}
      <Card className="p-6 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-8 mb-6">
            <TabsTrigger value="basic" data-testid="tab-basic">
              Basic Info
            </TabsTrigger>
            <TabsTrigger value="team" data-testid="tab-team">
              Project Team
            </TabsTrigger>
            <TabsTrigger value="sheets" data-testid="tab-sheets">
              Sheet Index
            </TabsTrigger>
            <TabsTrigger value="schedules" data-testid="tab-schedules">
              Schedules
            </TabsTrigger>
            <TabsTrigger value="notes" data-testid="tab-notes">
              General Notes
            </TabsTrigger>
            <TabsTrigger value="compliance" data-testid="tab-compliance">
              Compliance
            </TabsTrigger>
            <TabsTrigger value="renders" data-testid="tab-renders">
              Renders
            </TabsTrigger>
            <TabsTrigger value="visual" data-testid="tab-visual">
              Visual
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="basic" className="space-y-6">
            <BasicInfoEditor project={project} onChange={setProject} />
          </TabsContent>
          
          <TabsContent value="team" className="space-y-6">
            <ProjectTeamEditor project={project} onChange={setProject} />
          </TabsContent>
          
          <TabsContent value="sheets" className="space-y-6">
            <SheetIndexEditor project={project} onChange={setProject} />
          </TabsContent>
          
          <TabsContent value="schedules" className="space-y-6">
            <SchedulesEditor project={project} onChange={setProject} />
          </TabsContent>
          
          <TabsContent value="notes" className="space-y-6">
            <GeneralNotesEditor project={project} onChange={setProject} />
          </TabsContent>
          
          <TabsContent value="compliance" className="space-y-6">
            <ComplianceEditor project={project} onChange={setProject} />
          </TabsContent>
          
          <TabsContent value="renders" className="space-y-6">
            <RendersEditor project={project} onChange={setProject} />
          </TabsContent>
          
          <TabsContent value="visual" className="space-y-6">
            <VisualEditor project={project} onChange={setProject} />
          </TabsContent>
        </Tabs>
      </Card>
    </div>
  );
};

export default ProjectDetail;